'use client';

import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import Breadcrumbs from '@/components/ui/breadcrumbs';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const fadeLeft = {
  initial: { opacity: 0, x: -40 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const fadeRight = {
  initial: { opacity: 0, x: 40 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const services = [
  {
    id: 'dtg',
    title: 'Direct-to-Garment (DTG) Printing',
    subtitle: 'Sharp, Vibrant, No Minimum Order',
    description:
      'We specialise in Direct-to-Garment printing — an advanced technique that produces sharp, vibrant designs with exceptional detail. Premium inks ensure durable results without fading or cracking. Ideal for individual orders, photo prints, and complex multi-colour designs.',
    features: [
      'No minimum order required',
      'Photo-quality prints',
      'Soft hand feel on fabric',
      'Full-colour, unlimited colours',
    ],
  },
  {
    id: 'dtf',
    title: 'Direct-to-Film (DTF) Printing',
    subtitle: 'Versatile, Vibrant, Any Fabric',
    description:
      'Direct-to-Film printing transfers your design onto a special film before heat-pressing it onto the garment. This versatile method works on virtually any fabric type and colour, producing vivid, full-colour results with a smooth, flexible finish.',
    features: [
      'Works on any fabric colour',
      'No minimum order required',
      'Vibrant, full-colour results',
      'Durable and wash-resistant',
    ],
  },
  {
    id: 'embroidery',
    title: 'Embroidery',
    subtitle: 'Professional, Premium, Lasting',
    description:
      'Give your garments a professional, high-end finish with custom embroidery. Perfect for corporate workwear, school uniforms, polo shirts, and caps. Our in-house embroidery machines handle everything from simple logos to intricate designs.',
    features: [
      'In-house embroidery machines',
      'Caps, beanies, and flat garments',
      'School crests and corporate logos',
      'Premium, textured finish',
    ],
    ctaLink: '/embroidery',
    ctaText: 'View Embroidery Service',
  },
  {
    id: 'banners',
    title: 'Banner Printing',
    subtitle: 'Large Format, In-House, Fast Turnaround',
    description:
      'Professional PVC and mesh banner printing from our Birmingham print shop. Printed in-house using our Roland TrueVIS 8-colour printer for sharp, vibrant results. Perfect for construction sites, events, shop fronts, and promotions.',
    features: [
      '440gsm & 510gsm PVC options',
      'Wind-resistant mesh banners',
      'Eyelets included as standard',
      'Same-day service available',
    ],
    ctaLink: '/banner-printing',
    ctaText: 'View Banner Prices',
  },
];

const deliveryOptions = [
  {
    title: 'Express Delivery',
    description: 'Next working day after courier handoff.',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
      />
    ),
  },
  {
    title: 'Standard Delivery',
    description: 'Within five working days from order date.',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
      />
    ),
  },
  {
    title: 'International Delivery',
    description: 'Royal Mail tracked service, approximately ten working days.',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
      />
    ),
  },
];

const whyChooseUs = [
  { value: 'No Minimums', description: 'Order a single item or thousands — we accommodate all order sizes.' },
  { value: 'In-House', description: 'All printing and embroidery done on-site for quality control and speed.' },
  { value: 'Fast Turnaround', description: 'Most orders ready within 3–5 working days. Express available.' },
  { value: '10+ Years', description: 'Over a decade of experience in custom printing and embroidery.' },
];

export default function ServicesPage() {
  return (
    <div>
      <Breadcrumbs items={[{ label: 'Custom Printing' }]} />
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-black">
        <div className="container">
          <motion.div
            className="max-w-[48rem] mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4">
              What We Do
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Our Services
            </h1>
            <p className="text-lg lg:text-xl text-gray-300 leading-relaxed">
              High-quality custom clothing solutions with no minimum order requirements. From DTG and DTF printing to embroidery and large-format banner printing — all done in-house at our Birmingham workshop.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          className={`py-16 lg:py-24 ${index % 2 === 0 ? '' : 'bg-[var(--color-off-white)]'}`}
        >
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div className={index % 2 === 0 ? '' : 'lg:order-2'} {...(index % 2 === 0 ? fadeLeft : fadeRight)}>
                <p className="text-sm font-semibold uppercase tracking-widest text-[var(--color-text-muted)] mb-3">
                  {service.subtitle}
                </p>
                <h2 className="text-2xl lg:text-3xl font-bold mb-6">{service.title}</h2>
                <p className="text-[var(--color-text-secondary)] leading-relaxed mb-8">
                  {service.description}
                </p>
                {service.ctaLink && (
                  <Link
                    href={service.ctaLink}
                    className="inline-flex items-center justify-center px-8 py-3 text-sm font-semibold bg-brand-blue text-white hover:bg-brand-blue-hover transition-colors uppercase tracking-wider"
                  >
                    {service.ctaText}
                  </Link>
                )}
              </motion.div>
              <div className={index % 2 === 0 ? '' : 'lg:order-1'}>
                <div className="space-y-4">
                  {service.features.map((feature, i) => (
                    <motion.div
                      key={feature}
                      className="flex items-start gap-4 p-5 bg-white border border-[var(--color-border)]"
                      initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.1 }}
                    >
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-black text-white">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                          stroke="currentColor"
                          className="w-4 h-4"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium pt-1">{feature}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Why Choose Us */}
      <section className="py-16 lg:py-24 bg-[var(--color-warm-white)]">
        <div className="container">
          <motion.h2
            className="text-2xl lg:text-3xl font-bold text-center mb-12"
            {...fadeUp}
          >
            Why Choose OK T-Shirt?
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-[64rem] mx-auto">
            {whyChooseUs.map((item, i) => (
              <motion.div
                key={item.value}
                className="text-center p-8 bg-white border border-[var(--color-border)]"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.1 }}
              >
                <p className="text-2xl font-bold mb-2">{item.value}</p>
                <p className="text-sm text-[var(--color-text-secondary)]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Options */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">Delivery Options</h2>
            <p className="text-[var(--color-text-secondary)] max-w-[36rem] mx-auto">
              All delivery options apply to single and bulk orders. Local collection also available from our Great Barr workshop.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-[56rem] mx-auto">
            {deliveryOptions.map((option, i) => (
              <motion.div
                key={option.title}
                className="text-center p-8 border border-[var(--color-border)]"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.1 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-[var(--color-off-white)]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8"
                  >
                    {option.icon}
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{option.title}</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">{option.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Work Gallery */}
      <section className="py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">Our Work</h2>
            <p className="text-[var(--color-text-secondary)] max-w-[36rem] mx-auto">
              A selection of recent projects for businesses, schools, and teams across Birmingham and the West Midlands.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
            {[
              { label: 'Custom Printed T-Shirts', category: 'DTG', src: '/images/portfolio/imgi_14_ok-t-shirts-birmingham-customised-t-shirts.webp' },
              { label: 'DTG Printing In-House', category: 'DTG', src: '/images/portfolio/imgi_16_ok-t-shirts-birmingham-direct-to-garment-printing.webp' },
              { label: 'Quality You Can Trust', category: 'Quality', src: '/images/portfolio/imgi_15_ok-t-shirts-birmingham-quality-service.webp' },
              { label: 'Fast UK Delivery', category: 'Delivery', src: '/images/portfolio/imgi_17_ok-t-shirts-birmingham-fast-delivery.webp' },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                className="group relative aspect-square overflow-hidden border border-[var(--color-border)]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, ease: 'easeOut', delay: i * 0.05 }}
              >
                <Image
                  src={item.src}
                  alt={item.label}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300" />
                <span className="absolute top-2 left-2 bg-black text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1">
                  {item.category}
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-sm font-medium text-white">{item.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-black">
        <div className="container">
          <motion.div
            className="max-w-[64rem] mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Ready to Get
              <br className="hidden sm:block" />
              <span className="sm:hidden"> </span>
              Started?
            </h2>
            <p className="text-lg lg:text-xl text-gray-300 mb-10 leading-relaxed">
              Whether it&apos;s one t-shirt or a thousand — we&apos;re here to help bring your design to life.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact"
                className="group inline-flex items-center justify-center gap-2 px-10 py-4 text-base font-semibold bg-white text-black hover:bg-gray-100 transition-all duration-300"
              >
                Get a Quote
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <a
                href="tel:02045208521"
                className="inline-flex items-center justify-center px-10 py-4 text-base font-semibold border border-white text-white hover:bg-white hover:text-black transition-all duration-300"
              >
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
